import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, ReferenceDot,
} from "recharts";
import { TrendingUp, TrendingDown, Clock, Globe2, GitCompare, ArrowUp, ArrowDown, Star, X } from "lucide-react";
import { formatCurrency } from "@/lib/api";

const RANGES = [
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
  { days: 365, label: "1a" },
  { days: 1825, label: "5a" },
];

// Distinct palette for up to 4 compare lines. Primary uses trend color.
const COMPARE_COLORS = ["#8B6B1F", "#3B7BC6", "#B8447D", "#5F7368"];
const MAX_COMPARE = 4;

// Hoisted stable style objects (avoid new refs on every render)
const CHART_MARGIN = { top: 5, right: 10, left: 0, bottom: 0 };
const TOOLTIP_CONTENT_STYLE = {
  background: "#0F291E", border: "none", borderRadius: 8,
  color: "#FAFAF9", fontSize: 12,
};
const LEGEND_STYLE = { fontSize: 11 };
const ACTIVE_DOT = { r: 4 };
const CONTAINER_STYLE = { width: "100%", height: 300 };
const YAXIS_DOMAIN = ["auto", "auto"];

async function fetchHistory(id, params) {
  const r = await api.get(`/investments/${id}/price-history`, { params });
  return r.data;
}

export default function PriceHistoryDialog({ open, onOpenChange, investment, baseCurrency, allInvestments = [] }) {
  const [days, setDays] = useState(90);
  const [convert, setConvert] = useState(false);
  const [primary, setPrimary] = useState(null);
  const [compareIds, setCompareIds] = useState([]);              // array of investment ids
  const [compareData, setCompareData] = useState({});            // { id: response }
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addSel, setAddSel] = useState("");

  const compareOptions = allInvestments.filter(
    (i) => i.id !== investment?.id && i.type !== "real_estate"
  );
  const canAddMore = compareIds.length < MAX_COMPARE;
  const availableToAdd = compareOptions.filter((i) => !compareIds.includes(i.id));

  useEffect(() => {
    if (open) {
      setCompareIds([]);
      setCompareData({});
      setAddSel("");
    }
  }, [open, investment?.id]);

  useEffect(() => {
    if (!open || !investment?.id) return;
    let cancelled = false;
    api.get("/investments/compare-favorites", { params: { primary_id: investment.id, limit: 3 } })
      .then((r) => { if (!cancelled) setFavorites(r.data?.favorites || []); })
      .catch((e) => logger.warn("compare favorites", e));
    return () => { cancelled = true; };
  }, [open, investment?.id]);

  const loadPrimary = useCallback(async () => {
    if (!open || !investment?.id) return;
    setLoading(true);
    const params = { days };
    if (convert && baseCurrency && baseCurrency !== investment.currency) {
      params.convert_to = baseCurrency;
    }
    try {
      setPrimary(await fetchHistory(investment.id, params));
    } catch (e) {
      logger.error("history load", e);
    } finally {
      setLoading(false);
    }
  }, [open, investment, days, convert, baseCurrency]);

  useEffect(() => { loadPrimary(); }, [loadPrimary]);

  // Load history for every compare id. Runs whenever the set changes.
  useEffect(() => {
    if (!compareIds.length) { setCompareData({}); return; }
    let cancelled = false;
    (async () => {
      const results = {};
      for (const cid of compareIds) {
        const inv2 = allInvestments.find((i) => i.id === cid);
        if (!inv2) continue;
        const params = { days };
        if (convert && baseCurrency && baseCurrency !== inv2.currency) {
          params.convert_to = baseCurrency;
        }
        try {
          results[cid] = await fetchHistory(cid, params);
        } catch (e) {
          logger.error("compare load", cid, e);
        }
      }
      if (!cancelled) setCompareData(results);
    })();
    return () => { cancelled = true; };
  }, [compareIds, days, convert, baseCurrency, allInvestments]);

  const addCompare = (cid) => {
    if (!cid || compareIds.includes(cid) || compareIds.length >= MAX_COMPARE) return;
    setCompareIds([...compareIds, cid]);
    setAddSel("");
    api.post("/investments/compare-log", {
      primary_id: investment.id,
      compare_id: cid,
    }).catch((e) => logger.warn("compare log", e));
  };

  const removeCompare = (cid) => setCompareIds(compareIds.filter((x) => x !== cid));

  const isCompare = compareIds.length > 0;
  const points = React.useMemo(() => primary?.points || [], [primary]);
  const currency = primary?.currency || investment?.currency || "USD";
  const stats = primary?.stats;

  // Build merged chart data
  let chartData = points.map((p) => ({ date: p.date, primary: p.price }));
  if (isCompare) {
    const first1 = points[0]?.price;
    if (first1) {
      chartData = points.map((p) => ({
        date: p.date,
        primary: ((p.price - first1) / first1) * 100,
      }));
      compareIds.forEach((cid) => {
        const d = compareData[cid];
        if (!d?.points?.length) return;
        const first2 = d.points[0].price;
        if (!first2) return;
        const byDate = Object.fromEntries(d.points.map((p) => [p.date, p.price]));
        chartData = chartData.map((row) => ({
          ...row,
          [cid]: byDate[row.date] != null ? ((byDate[row.date] - first2) / first2) * 100 : null,
        }));
      });
    }
  }

  // Compute days with a daily drop of >= 10% vs the previous day. Only rendered
  // in single-series mode so the compare view stays readable.
  const dropDots = React.useMemo(() => {
    if (isCompare || points.length < 2) return [];
    const out = [];
    for (let i = 1; i < points.length; i += 1) {
      const prev = points[i - 1]?.price;
      const cur = points[i]?.price;
      if (!prev || !cur || prev <= 0) continue;
      const pct = ((cur - prev) / prev) * 100;
      if (pct <= -10) {
        out.push({ date: points[i].date, price: cur, pct });
      }
    }
    return out;
  }, [points, isCompare]);

  const compareName = (cid) => allInvestments.find((i) => i.id === cid)?.name || cid;
  const up = stats && stats.delta >= 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl bg-white" data-testid="price-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {investment?.name || "Historial"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {RANGES.map((r) => (
              <button
                key={r.days}
                type="button"
                data-testid={`history-range-${r.days}`}
                onClick={() => setDays(r.days)}
                className={`h-7 px-3 rounded-lg text-xs font-medium transition-colors ${
                  days === r.days ? "bg-[#0F291E] text-white" : "bg-[#FAFAF9] text-[#5F7368] hover:bg-[#EBEBEA]"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {baseCurrency && baseCurrency !== investment?.currency && (
              <label
                className="inline-flex items-center gap-2 text-xs text-[#5F7368] cursor-pointer select-none"
                data-testid="history-convert-toggle"
              >
                <input
                  type="checkbox"
                  checked={convert}
                  onChange={(e) => setConvert(e.target.checked)}
                  className="rounded border-[#EBEBEA]"
                />
                <Globe2 className="w-3.5 h-3.5" />
                Convertir a {baseCurrency}
              </label>
            )}
            {canAddMore && availableToAdd.length > 0 && (
              <div className="inline-flex items-center gap-1 text-xs text-[#5F7368]">
                <GitCompare className="w-3.5 h-3.5" />
                <select
                  data-testid="history-compare-select"
                  value={addSel}
                  onChange={(e) => { setAddSel(e.target.value); addCompare(e.target.value); }}
                  className="h-7 px-2 rounded-lg border border-[#EBEBEA] bg-white text-xs text-[#0A110E] focus:outline-none focus:border-[#1C7C54]/60 cursor-pointer max-w-[180px]"
                >
                  <option value="">Añadir…</option>
                  {availableToAdd.map((i) => (
                    <option key={i.id} value={i.id}>{i.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {favorites.length > 0 && !compareIds.length && (
          <div
            data-testid="history-favorites"
            className="flex items-center gap-2 flex-wrap text-[11px] text-[#5F7368] -mt-1"
          >
            <span className="inline-flex items-center gap-1 text-[#8A9992]">
              <Star className="w-3 h-3 text-[#C89A3F]" />
              Recientes:
            </span>
            {favorites.map((f) => (
              <button
                key={f.id}
                type="button"
                data-testid={`history-fav-${f.id}`}
                onClick={() => addCompare(f.id)}
                title={`Comparado ${f.count} ${f.count === 1 ? "vez" : "veces"}`}
                className="h-6 px-2 rounded-full border border-[#EBEBEA] text-[11px] font-medium bg-white text-[#0A110E] hover:border-[#1C7C54]/40 hover:bg-[#1C7C54]/5"
              >
                {f.name}
              </button>
            ))}
          </div>
        )}

        {compareIds.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap text-[11px]" data-testid="history-active-compare">
            {compareIds.map((cid, idx) => (
              <span
                key={cid}
                className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-white text-[11px] font-medium"
                style={{ background: COMPARE_COLORS[idx] }}
              >
                {compareName(cid)}
                <button
                  type="button"
                  onClick={() => removeCompare(cid)}
                  className="hover:opacity-70"
                  aria-label="Quitar"
                  data-testid={`history-remove-${cid}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="rounded-xl border border-[#EBEBEA] bg-white p-4">
          {loading ? (
            <div className="h-64 flex items-center justify-center text-sm text-[#8A9992]">Cargando…</div>
          ) : points.length < 2 ? (
            <div className="h-64 flex flex-col items-center justify-center gap-2 text-sm text-[#8A9992]">
              <Clock className="w-6 h-6" />
              <span>Todavía no hay historial suficiente.</span>
              <span className="text-xs">Se registra un snapshot cada día que actualices el precio.</span>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div>
                  <div className="text-2xl font-semibold tabular-nums text-[#0A110E]">
                    {isCompare ? `${(stats?.delta_pct ?? 0).toFixed(2)}%` : formatCurrency(stats?.last, currency)}
                  </div>
                  <div className={`flex items-center gap-1 text-sm font-medium ${up ? "text-[#1C7C54]" : "text-[#D95D39]"}`}>
                    {up ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                    {stats && !isCompare && (
                      <>{up ? "+" : ""}{stats.delta.toFixed(2)} ({stats.delta_pct.toFixed(2)}%)</>
                    )}
                    <span className="text-[#8A9992] font-normal ml-1">en {days}d</span>
                  </div>
                </div>
                {stats && !isCompare && (
                  <div className="flex items-center gap-4 text-[11px] text-[#5F7368]">
                    <span className="inline-flex items-center gap-1" data-testid="history-max">
                      <ArrowUp className="w-3 h-3 text-[#1C7C54]" />
                      Máx {formatCurrency(stats.max, currency)}
                      <span className="text-[#8A9992] ml-1">({stats.max_date.slice(5)})</span>
                    </span>
                    <span className="inline-flex items-center gap-1" data-testid="history-min">
                      <ArrowDown className="w-3 h-3 text-[#D95D39]" />
                      Mín {formatCurrency(stats.min, currency)}
                      <span className="text-[#8A9992] ml-1">({stats.min_date.slice(5)})</span>
                    </span>
                    {dropDots.length > 0 && (
                      <span
                        className="inline-flex items-center gap-1"
                        data-testid="history-drops-legend"
                        title={dropDots.map((d) => `${d.date}: ${d.pct.toFixed(1)}%`).join(", ")}
                      >
                        <span className="w-2 h-2 rounded-full bg-[#D95D39] inline-block" />
                        {dropDots.length} caída{dropDots.length !== 1 ? "s" : ""} ≥10%
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div style={CONTAINER_STYLE}>
                <ResponsiveContainer>
                  <LineChart data={chartData} margin={CHART_MARGIN}>
                    <CartesianGrid stroke="#EBEBEA" strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="date"
                      stroke="#8A9992"
                      fontSize={11}
                      tickMargin={6}
                      tickFormatter={(d) => (days > 365 ? d.slice(0, 7) : d.slice(5))}
                    />
                    <YAxis
                      stroke="#8A9992"
                      fontSize={11}
                      tickMargin={6}
                      domain={YAXIS_DOMAIN}
                      tickFormatter={(v) => (isCompare ? `${v.toFixed(0)}%` : v.toFixed(2))}
                    />
                    <Tooltip
                      contentStyle={TOOLTIP_CONTENT_STYLE}
                      formatter={(v, k) => {
                        if (v == null) return ["—", k];
                        const label = k === "primary" ? investment?.name : compareName(k);
                        return isCompare
                          ? [`${Number(v).toFixed(2)}%`, label]
                          : [formatCurrency(v, currency), "precio"];
                      }}
                      labelFormatter={(d) => `Fecha: ${d}`}
                    />
                    {isCompare && <Legend wrapperStyle={LEGEND_STYLE} />}
                    <Line
                      type="monotone"
                      dataKey="primary"
                      name={investment?.name || "Principal"}
                      stroke={up ? "#1C7C54" : "#D95D39"}
                      strokeWidth={2}
                      dot={false}
                      activeDot={ACTIVE_DOT}
                    />
                    {compareIds.map((cid, idx) => (
                      <Line
                        key={cid}
                        type="monotone"
                        dataKey={cid}
                        name={compareName(cid)}
                        stroke={COMPARE_COLORS[idx]}
                        strokeDasharray="4 2"
                        strokeWidth={2}
                        dot={false}
                        activeDot={ACTIVE_DOT}
                        connectNulls
                      />
                    ))}
                    {/* Highlight max/min points on the primary line (single-series mode only) */}
                    {!isCompare && stats && (
                      <>
                        <ReferenceDot
                          x={stats.max_date}
                          y={stats.max}
                          r={5}
                          fill="#1C7C54"
                          stroke="#FAFAF9"
                          strokeWidth={2}
                          isFront
                          data-testid="history-dot-max"
                        />
                        <ReferenceDot
                          x={stats.min_date}
                          y={stats.min}
                          r={5}
                          fill="#D95D39"
                          stroke="#FAFAF9"
                          strokeWidth={2}
                          isFront
                          data-testid="history-dot-min"
                        />
                      </>
                    )}
                    {/* Red dots on sharp-drop days (>=10% single-day decline) */}
                    {!isCompare && dropDots.map((d) => (
                      <ReferenceDot
                        key={`drop-${d.date}`}
                        x={d.date}
                        y={d.price}
                        r={4}
                        fill="#D95D39"
                        stroke="#FAFAF9"
                        strokeWidth={1.5}
                        isFront
                        data-testid={`history-dot-drop-${d.date}`}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="text-[11px] text-[#8A9992] text-right mt-2 flex justify-between">
                <span>
                  {primary?.converted && (
                    <>Convertido {primary.source_currency} → {primary.currency} (FX ECB día a día)</>
                  )}
                </span>
                <span>{points.length} puntos · {points[0]?.source || "n/d"}</span>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
